import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, AED_short, AED } from '../../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import MobileShell from '../../components/MobileShell';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';
import { Sparkles, ArrowLeft, ArrowRight, Loader2, ShieldAlert, Target, TrendingUp, Check } from 'lucide-react';

const CONDITIONS = [
  { key: 'excellent',     label: 'Excellent',      hint: 'Move-in ready' },
  { key: 'good',          label: 'Good',           hint: 'Light refresh' },
  { key: 'needs_refresh', label: 'Needs refresh',  hint: 'Paint/soft finishes' },
  { key: 'needs_refurb',  label: 'Full refurb',    hint: 'Kitchen/baths/floors' },
  { key: 'distressed',    label: 'Distressed',     hint: 'Down to shell' },
];

const TYPES = [
  { key: 'apartment', label: 'Apartment' },
  { key: 'penthouse', label: 'Penthouse' },
  { key: 'townhouse', label: 'Townhouse' },
  { key: 'villa',     label: 'Villa' },
  { key: 'commercial_office', label: 'Office' },
  { key: 'retail',    label: 'Retail' },
  { key: 'plot',      label: 'Plot' },
];

export default function MobileDealAnalyzer() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    property_type: 'apartment',
    community: 'Dubai Marina',
    building: '',
    bedrooms: 2,
    bathrooms: 2,
    size_sqft: 1200,
    asking_price_aed: 2000000,
    condition: 'good',
    view: '',
    floor: '',
    furnished: 'unfurnished',
  });

  useEffect(() => {
    if (!params || params.toString().length === 0) return;
    const patch = {};
    ['property_type','community','building','view','floor','condition','furnished','listing_url','notes']
      .forEach(k => { const v = params.get(k); if (v) patch[k] = v; });
    ['bedrooms','bathrooms','size_sqft','asking_price_aed']
      .forEach(k => { const v = params.get(k); if (v && !isNaN(parseFloat(v))) patch[k] = parseFloat(v); });
    const title = params.get('title');
    if (title && !patch.building) patch.building = title;
    if (Object.keys(patch).length) setForm(f => ({ ...f, ...patch }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const next = () => setStep(s => Math.min(s + 1, 4));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const analyze = async () => {
    setLoading(true);
    setStep(4);
    try {
      const r = await api.post('/deal/analyze', form);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setStep(0); };

  return (
    <MobileShell noHeader>
      {/* Header */}
      <div className="pt-3 pb-2 flex items-center gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Sparkles size={11} className="text-[#679286]"/> Iris AI Deal Analyzer
          </div>
          <h1 className="font-serif text-2xl leading-tight">Analyse a Dubai deal</h1>
        </div>
        {result && (
          <button onClick={reset} data-testid="m-da-reset" className="text-xs text-[#679286] font-medium">Start over</button>
        )}
      </div>

      {/* Step progress bar */}
      {!result && !loading && (
        <div className="flex gap-1.5 my-4" data-testid="m-da-progress" role="tablist">
          {[0,1,2,3].map(i => (
            <button
              key={i}
              onClick={() => step > i && setStep(i)}
              disabled={step <= i}
              aria-label={`Jump to step ${i + 1}`}
              data-testid={`m-da-progress-${i}`}
              className={`h-1.5 rounded-full flex-1 transition-colors ${
                step >= i ? 'bg-[#679286]' : 'bg-slate-200'
              } ${step > i ? 'cursor-pointer active:scale-95' : ''}`}
            />
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 0 && (
          <StepWrap key="s0" title="Where is it?" caption="Community, building, and property type.">
            <div className="space-y-3">
              <FieldCard label="Community">
                <PlacesAutocomplete
                  testid="m-da-community"
                  value={form.community}
                  onChange={(v) => setForm({ ...form, community: v })}
                  onPlace={(p) => setForm({ ...form, community: p.name || p.address })}
                  placeholder="e.g. Dubai Marina"
                  className="w-full text-base bg-transparent outline-none"
                />
              </FieldCard>
              <FieldCard label="Building / Project (optional)">
                <PlacesAutocomplete
                  testid="m-da-building"
                  value={form.building}
                  onChange={(v) => setForm({ ...form, building: v })}
                  onPlace={(p) => setForm({ ...form, building: p.name || p.address })}
                  placeholder="e.g. Marina Gate 1"
                  className="w-full text-base bg-transparent outline-none"
                />
              </FieldCard>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-2">Property type</div>
                <div className="grid grid-cols-3 gap-2">
                  {TYPES.map(t => (
                    <button
                      key={t.key}
                      onClick={() => setForm({ ...form, property_type: t.key })}
                      data-testid={`m-da-type-${t.key}`}
                      className={`py-3 rounded-2xl text-xs font-semibold border-2 transition-all ${
                        form.property_type === t.key ? 'border-[#679286] bg-[#EEF2F0] text-[#4d7266]' : 'border-slate-200 bg-white/70 text-slate-500'
                      }`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>
            </div>
          </StepWrap>
        )}

        {step === 1 && (
          <StepWrap key="s1" title="How big?" caption="Beds, baths and size.">
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Bedrooms" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} testid="m-da-beds" min={0} max={10}/>
              <NumField label="Bathrooms" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="m-da-baths" min={0} max={10} step={0.5}/>
              <div className="col-span-2">
                <FieldCard label="Built-up (sqft)">
                  <input
                    type="number" inputMode="numeric"
                    value={form.size_sqft}
                    onChange={(e) => setForm({ ...form, size_sqft: +e.target.value })}
                    data-testid="m-da-size"
                    className="w-full text-2xl font-serif bg-transparent outline-none"
                  />
                </FieldCard>
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-3">
                <FieldCard label="View / orientation">
                  <input
                    value={form.view}
                    onChange={(e) => setForm({ ...form, view: e.target.value })}
                    placeholder="Marina, Golf…"
                    data-testid="m-da-view"
                    className="w-full text-base bg-transparent outline-none placeholder:text-slate-400"
                  />
                </FieldCard>
                <FieldCard label="Floor">
                  <input
                    value={form.floor}
                    onChange={(e) => setForm({ ...form, floor: e.target.value })}
                    placeholder="high / 12"
                    data-testid="m-da-floor"
                    className="w-full text-base bg-transparent outline-none placeholder:text-slate-400"
                  />
                </FieldCard>
              </div>
            </div>
          </StepWrap>
        )}

        {step === 2 && (
          <StepWrap key="s2" title="What's the ask?" caption="Asking price in AED.">
            <FieldCard label="Asking price (AED)">
              <input
                type="number" inputMode="numeric"
                value={form.asking_price_aed}
                onChange={(e) => setForm({ ...form, asking_price_aed: +e.target.value })}
                data-testid="m-da-price"
                className="w-full text-3xl font-serif bg-transparent outline-none"
              />
              <div className="text-xs text-slate-500 mt-1">{AED(form.asking_price_aed)}</div>
            </FieldCard>
          </StepWrap>
        )}

        {step === 3 && (
          <StepWrap key="s3" title="What condition?" caption="Iris uses this to estimate refurb budget.">
            <div className="space-y-2">
              {CONDITIONS.map(c => {
                const on = form.condition === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setForm({ ...form, condition: c.key })}
                    data-testid={`m-da-condition-${c.key}`}
                    className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
                      on ? 'border-[#679286] bg-[#EEF2F0]' : 'border-slate-200 bg-white/70'
                    }`}
                  >
                    <div className="text-left">
                      <div className={`text-sm font-semibold ${on ? 'text-[#4d7266]' : 'text-slate-800'}`}>{c.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.hint}</div>
                    </div>
                    {on && <Check size={20} className="text-[#679286]"/>}
                  </button>
                );
              })}
            </div>
          </StepWrap>
        )}

        {(step === 4 || loading) && (
          <motion.div
            key="s4"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="pb-32"
            data-testid="m-da-result"
          >
            {loading && (
              <div className="py-20 flex flex-col items-center gap-4">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-[#679286] to-[#4d7266] grid place-items-center shadow-xl"
                >
                  <Sparkles size={22} className="text-white"/>
                </motion.div>
                <div className="text-sm text-slate-700 font-medium">Iris AI is analysing…</div>
                <div className="text-xs text-slate-500 max-w-xs text-center">Pulling Bayut, Property Finder & DLD comparables. Estimating refurb, ARV and deal score.</div>
              </div>
            )}
            {result && !loading && <MobileResult r={result}/>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky bottom nav for wizard */}
      {step < 4 && (
        <div className="fixed left-0 right-0 bottom-24 z-30 px-4 flex gap-2">
          {step > 0 && (
            <button
              onClick={prev}
              data-testid="m-da-prev"
              className="px-5 py-4 rounded-2xl bg-white text-slate-800 shadow-xl border border-slate-200 font-semibold flex items-center gap-2"
            >
              <ArrowLeft size={16}/> Back
            </button>
          )}
          {step < 3 ? (
            <button
              onClick={next}
              data-testid="m-da-next"
              className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-[#679286] to-[#4d7266] text-white font-semibold shadow-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              Next <ArrowRight size={16}/>
            </button>
          ) : (
            <button
              onClick={analyze}
              data-testid="m-da-analyze"
              className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-[#1A2530] to-[#3a4a58] text-white font-semibold shadow-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Sparkles size={16}/> Analyse with Iris AI
            </button>
          )}
        </div>
      )}
    </MobileShell>
  );
}

function StepWrap({ title, caption, children }) {
  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="pb-40">
      <h2 className="font-serif text-2xl leading-tight mb-1">{title}</h2>
      <p className="text-sm text-slate-500 mb-5">{caption}</p>
      {children}
    </motion.div>
  );
}

function FieldCard({ label, children }) {
  return (
    <div className="p-4 rounded-2xl bg-white/85 border border-white/70">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-1">{label}</div>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, testid, min = 0, max = 100, step = 1 }) {
  const [editing, setEditing] = useState(false);
  const dec = () => onChange(Math.max(min, +(value - step)));
  const inc = () => onChange(Math.min(max, +(value + step)));
  return (
    <div className="p-3 rounded-2xl bg-white/85 border border-white/70">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-1">{label}</div>
      <div className="flex items-center justify-between">
        <button
          onClick={dec}
          aria-label={`Decrease ${label}`}
          className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-lg font-bold text-slate-700 active:scale-95 transition-transform"
          data-testid={`${testid}-dec`}
        >−</button>
        {editing ? (
          <input
            autoFocus
            type="number"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(+e.target.value || 0)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
            data-testid={testid}
            className="font-serif text-2xl bg-transparent outline-none text-center w-16"
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label} directly`}
            className="font-serif text-2xl px-4"
            data-testid={testid}
          >{value}</button>
        )}
        <button
          onClick={inc}
          aria-label={`Increase ${label}`}
          className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-lg font-bold text-slate-700 active:scale-95 transition-transform"
          data-testid={`${testid}-inc`}
        >+</button>
      </div>
    </div>
  );
}

function MobileResult({ r }) {
  return (
    <div className="space-y-4">
      {/* Deal score hero */}
      <div className="p-6 rounded-3xl bg-gradient-to-br from-[#1A2530] to-[#3a4a58] text-white shadow-xl">
        <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">Iris AI Deal Score</div>
        <div className="flex items-baseline gap-3">
          <div className="font-serif text-6xl leading-none">{r.deal_score ?? '—'}</div>
          <div className="text-sm opacity-80">/ 100</div>
        </div>
        {r.deal_score_rationale?.verdict && (
          <p className="text-sm mt-3 leading-relaxed opacity-95">{r.deal_score_rationale.verdict}</p>
        )}
      </div>

      {/* Key numbers */}
      <div className="grid grid-cols-2 gap-2">
        <StatMini label="Fair price" value={AED_short(r.fair_price_aed)}/>
        <StatMini label="After refurb" value={AED_short(r.arv_aed)}/>
        <StatMini label="Refurb budget" value={AED_short(r.refurb_budget_aed)}/>
        <StatMini label="Target offer" value={AED_short(r.target_purchase_price_aed)} accent="green"/>
      </div>

      {/* Guardrails */}
      {r.walk_away_price_aed && (
        <div className="p-4 rounded-2xl bg-red-50 border border-red-100">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-medium flex items-center gap-1.5 mb-1"><ShieldAlert size={12}/> Walk-away price</div>
          <div className="font-serif text-2xl text-red-800">{AED(r.walk_away_price_aed)}</div>
          <div className="text-xs text-red-700/80 mt-1">Do not pay above this.</div>
        </div>
      )}

      {/* Summary */}
      {r.summary && (
        <div className="p-4 rounded-2xl bg-white/85 border border-white/70">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-1">Analysis</div>
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{r.summary}</p>
        </div>
      )}

      {/* Positives / Risks */}
      {r.positives?.length > 0 && (
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium mb-2 flex items-center gap-1.5"><TrendingUp size={12}/> Positives</div>
          <ul className="text-sm text-slate-800 space-y-1 list-disc pl-4">
            {r.positives.slice(0, 5).map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
      {r.risks?.length > 0 && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-100">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-medium mb-2 flex items-center gap-1.5"><Target size={12}/> Risks</div>
          <ul className="text-sm text-slate-800 space-y-1 list-disc pl-4">
            {r.risks.slice(0, 5).map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, accent }) {
  const clr = accent === 'green' ? 'text-emerald-700' : 'text-slate-900';
  return (
    <div className="p-4 rounded-2xl bg-white/85 border border-white/70">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-1">{label}</div>
      <div className={`font-serif text-xl ${clr}`}>{value ?? '—'}</div>
    </div>
  );
}
